/**
 * VFD sizing calculator: page controller for sections/vfd-calc-sizer.liquid
 * (Figma "Homepage Refresh (Copy)" 2701:114 calculator + 2701:300 matching drives, redesign Oct 2026).
 *
 * The sizing runs in the resources app: POST {api-base}/api/vfd-size, GET|POST {api-base}/api/vfd-size/ocr.
 * This file collects the inputs, calls the API (debounced as the form changes, and on submit), fills the
 * results card and its ledger from the response, then resolves matched models against the store
 * (/search/suggest.json, the sku-suggest section, /products/x.js) and renders the best in-store fits
 * as server-rendered cards (sections/vfd-calc-product-data.liquid).
 *
 * The VFD collection hero (snippets/vfd-sizer-compact.liquid) has its own controller, vfd-sizer-plp.js.
 */
(function () {
  var SUPPLY_VOLTAGES = [208, 230, 380, 400, 460, 480, 575, 690];
  var PROTOCOL_LABELS = {
    "modbus-rtu": "Modbus RTU",
    "modbus-tcp": "Modbus TCP",
    "ethernet-ip": "EtherNet/IP",
    profinet: "PROFINET",
    "profibus-dp": "PROFIBUS DP",
    bacnet: "BACnet",
    uss: "USS",
    devicenet: "DeviceNet",
    "cc-link": "CC-Link",
  };

  function $(root, sel) {
    return root.querySelector(sel);
  }

  function $$(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function apiHeaders(extra) {
    // No custom headers: a plain request needs no CORS preflight (the dev
    // tunnel header that used to live here forced one on every call).
    var h = {};
    if (extra) {
      for (var k in extra) h[k] = extra[k];
    }
    return h;
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function compressImage(file, maxEdge, quality) {
    maxEdge = maxEdge || 1600;
    quality = quality || 0.8;
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        var w = img.width;
        var h = img.height;
        var scale = Math.min(1, maxEdge / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        var canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not process image."));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          function (blob) {
            if (!blob) reject(new Error("Could not compress image."));
            else resolve(blob);
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error("Could not load image."));
      };
      img.src = url;
    });
  }

  function nearestVoltage(v) {
    var best = 460;
    var bestD = Infinity;
    for (var i = 0; i < SUPPLY_VOLTAGES.length; i++) {
      var d = Math.abs(SUPPLY_VOLTAGES[i] - v);
      if (d < bestD) {
        bestD = d;
        best = SUPPLY_VOLTAGES[i];
      }
    }
    return best;
  }

  function parseProtocolValue(raw) {
    var v = String(raw == null ? "" : raw).trim();
    return v || null;
  }

  function protocolLabel(id) {
    if (!id) return "";
    return PROTOCOL_LABELS[id] || id;
  }

  function normalizeProtocolToken(raw) {
    var s = String(raw || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    if (!s) return null;
    if (/modbus\s*rtu/.test(s)) return "modbus-rtu";
    if (/modbus\s*tcp/.test(s)) return "modbus-tcp";
    if (/bacnet/.test(s)) return "bacnet";
    if (/profinet/.test(s)) return "profinet";
    if (/profibus/.test(s)) return "profibus-dp";
    if (/ethernet\s*\/?\s*ip/.test(s) || s === "eip") return "ethernet-ip";
    if (/devicenet/.test(s)) return "devicenet";
    if (/cc[\s-]?link/.test(s)) return "cc-link";
    if (/\buss\b/.test(s)) return "uss";
    if (PROTOCOL_LABELS[s]) return s;
    return null;
  }

  function rowFieldbusList(r) {
    var fb = r && (r.fieldbus || r.protocols);
    if (!fb) return [];
    if (Array.isArray(fb)) {
      var out = [];
      for (var i = 0; i < fb.length; i++) {
        var token = normalizeProtocolToken(fb[i]);
        if (token && out.indexOf(token) === -1) out.push(token);
      }
      return out;
    }
    var parts = String(fb).split(/[,;/]|(?:\s+and\s+)/i);
    var list = [];
    for (var j = 0; j < parts.length; j++) {
      var t = normalizeProtocolToken(parts[j]);
      if (t && list.indexOf(t) === -1) list.push(t);
    }
    return list;
  }

  function formatFieldbusLabels(r) {
    var list = rowFieldbusList(r);
    if (!list.length) return "";
    return list.map(protocolLabel).join(", ");
  }

  function applyProtocolFilter(result, protocol) {
    if (!protocol || !result) {
      return { result: result, note: null, warn: false };
    }
    var matches = result.matches || {};
    var all = flattenMatches(matches);
    var hasData = false;
    for (var i = 0; i < all.length; i++) {
      if (rowFieldbusList(all[i]).length) {
        hasData = true;
        break;
      }
    }
    if (!hasData) {
      return {
        result: result,
        note:
          "Protocol preference noted (" +
          protocolLabel(protocol) +
          "). Comms data is not yet in the catalog for these drives — confirm against the datasheet before ordering.",
        warn: false,
      };
    }

    var filtered = {};
    var total = 0;
    var brands = Object.keys(matches);
    for (var b = 0; b < brands.length; b++) {
      var brand = brands[b];
      var rows = matches[brand] || [];
      var kept = [];
      for (var r = 0; r < rows.length; r++) {
        var list = rowFieldbusList(rows[r]);
        if (list.length && list.indexOf(protocol) !== -1) kept.push(rows[r]);
      }
      if (kept.length) {
        filtered[brand] = kept;
        total += kept.length;
      }
    }

    if (!total) {
      return {
        result: result,
        note:
          "No catalog drives list " +
          protocolLabel(protocol) +
          " at this amp rating. Showing all amp matches — ask an engineer to confirm comms options.",
        warn: true,
      };
    }

    var next = {};
    for (var k in result) {
      if (Object.prototype.hasOwnProperty.call(result, k)) next[k] = result[k];
    }
    next.matches = filtered;
    next.matchTotals = {};
    var fb = Object.keys(filtered);
    for (var f = 0; f < fb.length; f++) {
      next.matchTotals[fb[f]] = filtered[fb[f]].length;
    }
    return {
      result: next,
      note:
        "Filtered to " +
        protocolLabel(protocol) +
        ". Verify built-in vs optional comm module on the datasheet.",
      warn: false,
    };
  }

  function flattenMatches(matches) {
    var all = [];
    var brands = Object.keys(matches || {});
    for (var i = 0; i < brands.length; i++) {
      var brand = brands[i];
      var rows = matches[brand] || [];
      for (var j = 0; j < rows.length; j++) {
        var r = rows[j];
        all.push({
          brand: r.brand || brand,
          series: r.series,
          model: r.model,
          kw: r.kw,
          hp: r.hp,
          currentA: r.currentA,
          matchAmps: r.matchAmps,
          utilisation: r.utilisation,
          enclosure: r.enclosure,
          duty: r.duty,
          dutyDowngrade: r.dutyDowngrade,
          ndUnavailable: r.ndUnavailable,
          frame: r.frame,
          voltageClass: r.voltageClass,
          fieldbus: r.fieldbus,
          protocols: r.protocols,
          source: r.source,
        });
      }
    }
    return all;
  }

  function fitScore(r, result) {
    var amps = r.matchAmps != null ? r.matchAmps : r.currentA;
    var score = 0;
    if (result.requiredA != null && amps != null) {
      var over = Number(amps) - Number(result.requiredA);
      score += over >= 0 ? over : Math.abs(over) * 20;
    } else {
      score += 500;
    }
    if (r.ndUnavailable || r.dutyDowngrade) score += 5;
    return score;
  }

  function isWashdown(r) {
    return /IP\s*66|NEMA\s*4|washdown/i.test(String(r.enclosure || ""));
  }

  function modelHref(storeSearch, model) {
    return storeSearch + encodeURIComponent(model);
  }

  function productHref(handle, variantId) {
    if (!handle) return "";
    var url = "/products/" + encodeURIComponent(handle);
    if (variantId) url += "?variant=" + encodeURIComponent(variantId);
    return url;
  }

  function formatMoneyCents(cents) {
    if (cents == null || cents === "") return "";
    var n = Number(cents);
    if (isNaN(n)) return "";
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(n, theme.moneyFormat || "${{amount}}");
      }
      if (window.Shopify && Shopify.formatMoney) {
        return Shopify.formatMoney(n, (theme && theme.moneyFormat) || "${{amount}}");
      }
    } catch (e) {}
    return "$" + (n / 100).toFixed(2);
  }

  function normSku(s) {
    return String(s || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
  }

  var catalogCache = {};
  var productJsCache = {};

  function fetchProductJs(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productJsCache[handle]) {
      productJsCache[handle] = fetch(
        "/products/" + encodeURIComponent(handle) + ".js",
        { credentials: "same-origin" }
      )
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .catch(function () {
          return null;
        });
    }
    return productJsCache[handle];
  }

  function pickBestVariant(product, model) {
    if (!product || !product.variants || !product.variants.length) return null;
    var target = normSku(model);
    var variants = product.variants;
    var i;
    for (i = 0; i < variants.length; i++) {
      if (normSku(variants[i].sku) === target && target) return variants[i];
    }
    for (i = 0; i < variants.length; i++) {
      if (
        target &&
        normSku(variants[i].sku).indexOf(target) !== -1
      ) {
        return variants[i];
      }
    }
    for (i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0];
  }

  function scoreSuggestProduct(product, model) {
    var target = normSku(model);
    if (!target) return 0;
    var score = 0;
    var title = normSku(product.title);
    var handle = normSku(product.handle);
    var variants = product.variants || [];
    var i;
    for (i = 0; i < variants.length; i++) {
      var sku = normSku(variants[i].sku);
      if (sku === target) score = Math.max(score, 100);
      else if (sku.indexOf(target) !== -1 || target.indexOf(sku) !== -1)
        score = Math.max(score, 80);
    }
    if (title === target) score = Math.max(score, 95);
    else if (title.indexOf(target) !== -1) score = Math.max(score, 70);
    if (handle.indexOf(target) !== -1) score = Math.max(score, 60);
    return score;
  }

  function resolveFromSuggestJson(model) {
    var fields =
      "title,product_type,variants.title,variants.sku,vendor,tag";
    var url =
      "/search/suggest.json?q=" +
      encodeURIComponent(model) +
      "&resources[type]=product&resources[limit]=6&resources[options][fields]=" +
      encodeURIComponent(fields);
    return fetch(url, { credentials: "same-origin" })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var products =
          (data &&
            data.resources &&
            data.resources.results &&
            data.resources.results.products) ||
          [];
        if (!products.length) return null;
        var best = null;
        var bestScore = 0;
        for (var i = 0; i < products.length; i++) {
          var s = scoreSuggestProduct(products[i], model);
          if (s > bestScore) {
            bestScore = s;
            best = products[i];
          }
        }
        // Require a real SKU/title hit; avoid random first result
        if (!best || bestScore < 60) return null;
        return best;
      })
      .catch(function () {
        return null;
      });
  }

  function resolveFromSkuSuggestSection(model) {
    var url =
      "/search/suggest?q=" +
      encodeURIComponent(model) +
      "&section_id=sku-suggest&resources[type]=product";
    return fetch(url, { credentials: "same-origin" })
      .then(function (r) {
        return r.ok ? r.text() : "";
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var rows = doc.querySelectorAll("[data-sku][data-handle]");
        var target = normSku(model);
        var best = null;
        var bestScore = 0;
        for (var i = 0; i < rows.length; i++) {
          var el = rows[i];
          var sku = normSku(el.getAttribute("data-variant-sku") || el.getAttribute("data-sku"));
          var title = normSku(el.getAttribute("data-title"));
          var handle = el.getAttribute("data-handle") || "";
          var score = 0;
          if (sku === target) score = 100;
          else if (sku.indexOf(target) !== -1) score = 80;
          else if (title.indexOf(target) !== -1) score = 70;
          else if (normSku(handle).indexOf(target) !== -1) score = 60;
          if (score > bestScore) {
            bestScore = score;
            best = {
              handle: handle,
              variantId: el.getAttribute("data-variant-id"),
              productId: el.getAttribute("data-product-id"),
              price: el.getAttribute("data-price"),
              title: el.getAttribute("data-title"),
              sku: el.getAttribute("data-variant-sku") || el.getAttribute("data-sku"),
            };
          }
        }
        if (!best || bestScore < 60) return null;
        return best;
      })
      .catch(function () {
        return null;
      });
  }

  function resolveCatalog(model) {
    var key = String(model || "").trim();
    if (!key) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(catalogCache, key)) {
      return catalogCache[key];
    }
    catalogCache[key] = resolveFromSuggestJson(key)
      .then(function (hit) {
        if (hit && hit.handle) {
          return fetchProductJs(hit.handle).then(function (prod) {
            if (!prod) {
              return {
                handle: hit.handle,
                variantId: hit.variants && hit.variants[0] && hit.variants[0].id,
                // suggest.json prices are dollar strings; everything else here is in cents
                price: hit.price != null && hit.price !== "" ? Math.round(Number(hit.price) * 100) : null,
                available: !!hit.available,
                title: hit.title,
                url: hit.url || productHref(hit.handle),
              };
            }
            var v = pickBestVariant(prod, key);
            if (!v) return null;
            return {
              handle: prod.handle,
              productId: prod.id,
              variantId: v.id,
              price: v.price,
              available: !!v.available,
              title: prod.title,
              sku: v.sku,
              url: productHref(prod.handle, v.id),
            };
          });
        }
        return resolveFromSkuSuggestSection(key).then(function (sec) {
          if (!sec || !sec.handle) return null;
          return fetchProductJs(sec.handle).then(function (prod) {
            if (prod) {
              var v = pickBestVariant(prod, key);
              if (v) {
                return {
                  handle: prod.handle,
                  variantId: v.id,
                  price: v.price,
                  available: !!v.available,
                  title: prod.title,
                  sku: v.sku,
                  url: productHref(prod.handle, v.id),
                };
              }
            }
            return {
              handle: sec.handle,
              productId: sec.productId,
              variantId: sec.variantId,
              price: sec.price != null ? Number(sec.price) : null,
              available: null,
              title: sec.title,
              sku: sec.sku,
              url: productHref(sec.handle, sec.variantId),
            };
          });
        });
      })
      .catch(function () {
        return null;
      });
    return catalogCache[key];
  }

  function mapPool(items, limit, worker) {
    var i = 0;
    var results = new Array(items.length);
    function next() {
      if (i >= items.length) return Promise.resolve();
      var idx = i++;
      return Promise.resolve(worker(items[idx], idx)).then(function (val) {
        results[idx] = val;
        return next();
      });
    }
    var runners = [];
    for (var r = 0; r < Math.min(limit, items.length); r++) runners.push(next());
    return Promise.all(runners).then(function () {
      return results;
    });
  }

  // ---------------------------------------------------------------------------
  // Page controller (sections/vfd-calc-sizer.liquid, Figma 2701:114 + 2701:300)
  // ---------------------------------------------------------------------------

  var LOAD_LABELS = {
    variable: "fan / pump",
    constant: "conveyor / compressor",
    highInertia: "crane / mill",
  };
  var STARTS_FACTOR = 1.05; // frequent starts, over 10 per hour
  var MOTORS_FACTOR = 1.25; // several motors on one drive (sum of FLAs)
  var CARD_TARGET = 3; // product cards in 03 / Matching drives
  var RESOLVE_LIMIT = 40; // most best-fit rows tried against the store for those cards
  var LIST_PAGE = 20; // rows per "Show more" in the full list
  var DEBOUNCE_MS = 650;
  var NUMBER_WORDS = ["No", "One", "Two", "Three"];

  function fixed(n, d) {
    var x = Number(n);
    return isFinite(x) ? x.toFixed(d) : "—";
  }

  function trimNum(n, d) {
    var x = Number(n);
    if (!isFinite(x)) return "—";
    return String(Math.round(x * Math.pow(10, d)) / Math.pow(10, d));
  }

  function setText(root, sel, text) {
    var el = $(root, sel);
    if (el) el.textContent = text;
    return el;
  }

  function radioValue(form, name) {
    var el = form.querySelector('input[name="' + name + '"]:checked');
    return el ? el.value : null;
  }

  function setRadio(form, name, value) {
    var el = form.querySelector('input[name="' + name + '"][value="' + value + '"]');
    if (el) el.checked = true;
    return !!el;
  }

  function voltageClassLabel(cls, volts) {
    var map = {
      "230V-3ph-in": "200–240 V",
      "230V-1ph-in": "230 V single-phase",
      "460V-3ph-in": "380–480 V",
      "575V-3ph-in": "575–600 V",
      "690V-3ph-in": "690 V",
    };
    if (cls && map[cls]) return map[cls];
    if (cls) return String(cls).replace(/-in$/, "").replace("V-", " V ").replace("3ph", "three-phase").replace("1ph", "single-phase");
    return volts + " V";
  }

  function rowAmps(r) {
    return r.matchAmps != null ? r.matchAmps : r.currentA;
  }

  function rowSpecs(r, result) {
    var bits = [];
    if (r.hp != null) bits.push(trimNum(r.hp, 1) + " HP");
    var a = rowAmps(r);
    if (a != null) bits.push(trimNum(a, 1) + " A");
    bits.push(voltageClassLabel(r.voltageClass || (result.voltage && result.voltage.voltageClass), ""));
    if (r.duty) bits.push(r.duty === "heavy" ? "Heavy duty" : "Normal duty");
    if (r.enclosure) bits.push(r.enclosure);
    var fb = formatFieldbusLabels(r);
    if (fb) bits.push(fb);
    return bits.join(" · ");
  }

  function fitBadge(pick, result) {
    var r = pick.row;
    var a = rowAmps(r);
    var aTxt = a != null ? trimNum(a, 1) + " A" : "";
    if (pick.best && a != null && result.requiredA) {
      var head = Math.max(0, Math.round((Number(a) / Number(result.requiredA) - 1) * 100));
      return { text: "Best fit · " + aTxt + " · " + head + "% headroom", tone: "best" };
    }
    if (pick.label === "Washdown / IP66") return { text: "Washdown / IP66 · " + aTxt, tone: "info" };
    if (r.duty === "heavy" || r.ndUnavailable) return { text: "Heavy-duty rating · " + aTxt, tone: "info" };
    if (pick.label === "Alt brand") return { text: "Other brand · " + aTxt, tone: "info" };
    return { text: "Next size up · " + aTxt, tone: "info" };
  }

  // ---------------------------------------------------------------------------
  // Form state + validation (WCAG 3.3.1 / 3.3.3: message linked, input aria-invalid, focus first)
  // ---------------------------------------------------------------------------

  function readState(form) {
    var get = function (name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? String(el.value || "").trim() : "";
    };
    var flaRaw = get("fla");
    var ambientRaw = get("ambientC");
    var altitudeRaw = get("altitudeM");
    return {
      powerRaw: get("power"),
      power: Number(get("power")),
      powerUnit: radioValue(form, "powerUnit") || "hp",
      flaRaw: flaRaw,
      fla: flaRaw === "" ? null : Number(flaRaw),
      voltage: Number(radioValue(form, "supplyVoltage") || 460),
      phases: radioValue(form, "phases") === "1" ? 1 : 3,
      sfShown: Number(radioValue(form, "serviceFactor") || 1.15),
      application: radioValue(form, "application") || "variable",
      starts: radioValue(form, "starts") || "occasional",
      motors: radioValue(form, "motors") || "one",
      ambientC: ambientRaw === "" ? 40 : Number(ambientRaw),
      altitudeM: altitudeRaw === "" ? 0 : Number(altitudeRaw),
      protocol: parseProtocolValue(get("protocol")),
    };
  }

  function validate(s) {
    var e = {};
    if (!(s.power > 0)) e.power = "Enter the motor power from the nameplate, in HP or kW.";
    if (s.fla !== null && !(s.fla > 0)) e.fla = "Enter the full-load amps as a positive number, or leave it blank.";
    if (!isFinite(s.ambientC) || s.ambientC < -20 || s.ambientC > 60) e.ambient = "Enter the enclosure ambient between −20 and 60 °C.";
    if (!isFinite(s.altitudeM) || s.altitudeM < 0 || s.altitudeM > 5000) e.altitude = "Enter the site altitude between 0 and 5,000 m.";
    if (s.phases === 1 && s.voltage >= 380) {
      e.phases = s.voltage + " V is not a standard single-phase supply. Use 208 or 230 V for 1-phase, or switch to 3-phase.";
    }
    return e;
  }

  var FIELD_INPUT = { power: "power", fla: "fla", ambient: "ambientC", altitude: "altitudeM", phases: "phases" };

  function showErrors(root, errors, focusFirst) {
    var first = null;
    $$(root, "[data-vfd-err]").forEach(function (msg) {
      var key = msg.getAttribute("data-vfd-err");
      var text = errors[key];
      var field = msg.closest("[data-vfd-field]");
      var input = root.querySelector('[name="' + FIELD_INPUT[key] + '"]' + (key === "phases" ? '[value="1"]' : ""));
      if (text) {
        var span = msg.querySelector("span");
        if (span) span.textContent = text;
        msg.hidden = false;
        if (field) field.classList.add("is-invalid");
        if (input) input.setAttribute("aria-invalid", "true");
        if (!first && input) first = key === "phases" ? root.querySelector('[name="phases"]:checked') : input;
      } else {
        msg.hidden = true;
        if (field) field.classList.remove("is-invalid");
        if (input) input.removeAttribute("aria-invalid");
      }
    });
    if (focusFirst && first) first.focus();
  }

  // ---------------------------------------------------------------------------
  // Sizing call. Frequent starts / several motors scale the FLA that is sent, so the required amps AND the
  // matched drives include them; with FLA blank the NEC estimate is fetched first. Service factor is sent
  // as 1.00 (decision 12 Oct 2026): the drive limits current, so SF is not a sizing multiplier.
  // ---------------------------------------------------------------------------

  function postSize(apiBase, body) {
    return fetch(apiBase + "/api/vfd-size", {
      method: "POST",
      headers: apiHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.text().then(function (txt) {
        var data = null;
        try {
          data = JSON.parse(txt);
        } catch (e) {}
        if (!r.ok || !data || data.error) {
          throw new Error((data && data.error) || "Could not size the drive. Check your connection and try again.");
        }
        return data;
      });
    });
  }

  function sizeDrive(apiBase, s) {
    var extra = (s.starts === "frequent" ? STARTS_FACTOR : 1) * (s.motors === "several" ? MOTORS_FACTOR : 1);
    var body = {
      power: s.power,
      powerUnit: s.powerUnit,
      fla: s.fla,
      supplyVoltage: s.voltage,
      phases: s.phases,
      serviceFactor: 1,
      application: s.application,
      ambientC: s.ambientC,
      altitudeM: s.altitudeM,
    };
    if (s.protocol) body.protocols = [s.protocol];

    function finish(flaBase, estimated) {
      if (extra === 1) {
        return postSize(apiBase, body).then(function (d) {
          return { data: d, flaBase: d.flaUsed != null ? d.flaUsed : flaBase, estimated: !!d.flaEstimated, extra: 1 };
        });
      }
      var scaled = {};
      for (var k in body) scaled[k] = body[k];
      scaled.fla = Math.round(flaBase * extra * 100) / 100;
      return postSize(apiBase, scaled).then(function (d) {
        return { data: d, flaBase: flaBase, estimated: estimated, extra: extra };
      });
    }

    if (extra !== 1 && s.fla === null) {
      return postSize(apiBase, body).then(function (first) {
        return finish(Number(first.flaUsed), true);
      });
    }
    return finish(s.fla, false);
  }

  function isSoftStarter(r) {
    return /soft-?starter/i.test(String(r.source || "") + " " + String(r.series || ""));
  }

  /** Smallest VFD rating at or above the required amps (the API's selectedDriveRating can be a soft starter). */
  function nearestRating(d) {
    var best = null;
    flattenMatches(d.matches).forEach(function (r) {
      var a = Number(rowAmps(r));
      if (isSoftStarter(r) || !isFinite(a) || a < Number(d.requiredA)) return;
      if (!best || a < best.amps) best = { amps: a, hp: r.hp };
    });
    return best || d.selectedDriveRating || null;
  }

  // ---------------------------------------------------------------------------
  // Results card
  // ---------------------------------------------------------------------------

  function factorText(f, divide) {
    var x = Number(f);
    if (!isFinite(x) || Math.abs(x - 1) < 0.0005) return "× 1.00";
    return (divide ? "÷ " : "× ") + x.toFixed(2);
  }

  function renderEmptyResult(root) {
    setText(root, '[data-vfd-out="amps"]', "— A");
    setText(root, '[data-vfd-out="context"]', "Enter the motor power to size the drive");
    ["fla", "margin", "ambient", "altitude", "rating"].forEach(function (k) {
      setText(root, '[data-vfd-out="' + k + '"]', "—");
    });
  }

  function renderResult(root, s, sized) {
    var d = sized.data;
    var powerTxt = trimNum(s.power, 2) + (s.powerUnit === "kw" ? " kW" : " HP");
    var flaTxt = sized.estimated ? "FLA estimated" : fixed(sized.flaBase, 0) + " A FLA";
    if (!sized.estimated && Math.round(sized.flaBase) !== Number(sized.flaBase)) flaTxt = trimNum(sized.flaBase, 1) + " A FLA";
    setText(root, '[data-vfd-out="context"]', [powerTxt, flaTxt, s.voltage + " V", LOAD_LABELS[s.application]].join(" · "));
    setText(root, '[data-vfd-out="amps"]', fixed(d.requiredA, 1) + " A");

    var heavy = d.dutyColumn === "hd";
    setText(root, '[data-vfd-out="duty"]', heavy ? "Heavy duty" : "Normal duty");
    setText(root, '[data-vfd-out="dutyNote"]', heavy ? "150% for 60 s" : "110% for 60 s");
    setText(root, '[data-vfd-out="voltage"]', s.voltage + " V · " + s.phases + "-ph");

    var flaEl = setText(root, '[data-vfd-out="fla"]', fixed(sized.flaBase, 1) + " A" + (sized.estimated ? " · estimated" : ""));
    if (flaEl) flaEl.classList.toggle("is-warn", sized.estimated);
    var flaNote = $(root, '[data-vfd-out="flaNote"]');
    if (flaNote) {
      flaNote.hidden = !sized.estimated;
      flaNote.textContent = sized.estimated
        ? "Estimated from NEC Table 430.250 for " + powerTxt + " at " + s.voltage + " V. Nameplate FLA can differ by several amps — confirm before ordering."
        : "";
    }

    setText(root, '[data-vfd-out="marginLabel"]', "Duty margin · " + LOAD_LABELS[s.application]);
    setText(root, '[data-vfd-out="margin"]', factorText(1 + Number(d.margin || 0)));
    setText(root, '[data-vfd-out="sfLabel"]', "Service factor " + s.sfShown.toFixed(2));
    var startsRow = $(root, "[data-vfd-ledger-starts]");
    if (startsRow) startsRow.hidden = s.starts !== "frequent";
    var motorsRow = $(root, "[data-vfd-ledger-motors]");
    if (motorsRow) motorsRow.hidden = s.motors !== "several";
    setText(root, '[data-vfd-out="ambientLabel"]', "Ambient " + trimNum(s.ambientC, 1) + " °C");
    setText(root, '[data-vfd-out="ambient"]', factorText(d.derateTemp != null ? d.derateTemp : 1, true));
    setText(root, '[data-vfd-out="altitudeLabel"]', "Altitude " + Number(s.altitudeM).toLocaleString("en-US") + " m");
    setText(root, '[data-vfd-out="altitude"]', factorText(d.derateAlt != null ? d.derateAlt : 1, true));
    var phaseRow = $(root, "[data-vfd-ledger-phase]");
    var pf = Number(d.phaseFactor || 1);
    if (phaseRow) phaseRow.hidden = Math.abs(pf - 1) < 0.0005;
    setText(root, '[data-vfd-out="phase"]', factorText(pf));

    var sel = nearestRating(d);
    setText(
      root,
      '[data-vfd-out="rating"]',
      sel && sel.amps != null
        ? trimNum(sel.amps, 1) + " A" + (sel.hp != null ? " · " + Math.round(sel.hp) + " HP class" : "")
        : "No standard rating"
    );

    var notes = [];
    if (d.voltage && d.voltage.note) notes.push(d.voltage.note + ".");
    // With starts/motors applied the API sees the scaled FLA, so its "FLA looks high" check would misfire
    if (d.flaWarning && sized.extra === 1) notes.push(d.flaWarning);
    if (d.derateWarning) notes.push(d.derateWarning);
    var noteEl = $(root, "[data-vfd-sizing-note]");
    if (noteEl) {
      noteEl.textContent = notes.join(" ");
      noteEl.hidden = !notes.length;
    }

    var savings = $(root, "[data-vfd-savings-link]");
    if (savings) {
      var base = root.getAttribute("data-savings-url") || "/pages/vfd-energy-savings-calculator";
      var q = (s.powerUnit === "kw" ? "kw=" : "hp=") + encodeURIComponent(s.power) +
        "&v=" + s.voltage +
        "&load=" + (s.application === "variable" ? "variable" : "constant");
      if (!sized.estimated && sized.flaBase) q += "&fla=" + encodeURIComponent(trimNum(sized.flaBase, 1));
      savings.href = base + (base.indexOf("?") === -1 ? "?" : "&") + q;
    }
  }

  // ---------------------------------------------------------------------------
  // 03 / Matching drives: three best in-store fits as product cards, then the full list
  // ---------------------------------------------------------------------------

  var cardCache = {};

  function fetchCard(handle, variantId) {
    var key = handle + "|" + (variantId || "");
    if (!cardCache[key]) {
      var url = "/products/" + encodeURIComponent(handle) + "?section_id=vfd-calc-product-data" +
        (variantId ? "&variant=" + encodeURIComponent(variantId) : "");
      cardCache[key] = fetch(url, { credentials: "same-origin" })
        .then(function (r) {
          return r.ok ? r.text() : "";
        })
        .then(function (html) {
          if (!html) return null;
          var doc = new DOMParser().parseFromString(html, "text/html");
          return doc.querySelector(".vc-pcard");
        })
        .catch(function () {
          return null;
        });
    }
    return cardCache[key].then(function (node) {
      return node ? node.cloneNode(true) : null;
    });
  }

  function loadingCards(grid, n) {
    grid.innerHTML = "";
    for (var i = 0; i < n; i++) {
      var li = document.createElement("li");
      li.innerHTML =
        '<article class="vc-pcard is-loading" aria-hidden="true"><div class="vc-pcard__photo"></div>' +
        '<div class="vc-pcard__body"><p class="vc-pcard__eyebrow">&nbsp;</p><p class="vc-pcard__title">&nbsp;</p>' +
        '<p class="vc-pcard__specs">&nbsp;</p></div></article>';
      grid.appendChild(li);
    }
  }

  function quoteHref(root, text) {
    var base = root.getAttribute("data-quote-url") || "/pages/quote";
    return base + (base.indexOf("?") >= 0 ? "&" : "?") + "q=" + encodeURIComponent(text);
  }

  function renderEmpty(root, s, d, catalogCount) {
    var box = $(root, "[data-vfd-matches-empty]");
    if (!box) return;
    var amps = fixed(d.requiredA, 1) + " A";
    var subject = "VFD for " + trimNum(s.power, 2) + (s.powerUnit === "kw" ? " kW" : " HP") + ", " + amps + " continuous, " + s.voltage + " V " + s.phases + "-ph";
    var html =
      '<div class="vc-empty">' +
      '<h3 class="vc-state__title">Nothing on the shelf matches ' + escapeHtml(amps) + " at " + s.voltage + " V</h3>" +
      '<p class="vc-state__body">' +
      escapeHtml(d.emptyMessage || "The sizing still stands. Send it to us and an engineer will quote a drive from our authorized lines, usually within the hour.") +
      "</p>" +
      '<div class="vc-actions"><a class="vc-btn vc-btn--primary" href="' + escapeHtml(quoteHref(root, subject)) + '">Request a quote for this sizing</a>' +
      (catalogCount ? '<button type="button" class="vc-btn vc-btn--secondary" data-vfd-open-list>See all ' + catalogCount + " catalog matches</button>" : "") +
      "</div></div>";
    box.innerHTML = html;
    box.hidden = false;
    var open = $(box, "[data-vfd-open-list]");
    if (open) {
      open.addEventListener("click", function () {
        var toggle = $(root, "[data-vfd-matches-toggle]");
        if (toggle) toggle.click();
      });
    }
  }

  function listRowHtml(r, storeSearch) {
    var a = rowAmps(r);
    var sub = [r.brand, r.series].filter(Boolean).join(" · ");
    return (
      '<tr data-vfd-model="' + escapeHtml(r.model) + '">' +
      '<td><a href="' + escapeHtml(modelHref(storeSearch, r.model)) + '" data-vfd-product-link>' + escapeHtml(r.model) + "</a>" +
      '<span class="vc-mlist__sub">' + escapeHtml(sub) + "</span></td>" +
      '<td class="is-num" data-label="Rating">' + (a != null ? escapeHtml(trimNum(a, 1)) + " A" : "—") +
      (r.hp != null ? '<span class="vc-mlist__sub">' + escapeHtml(trimNum(r.hp, 1)) + " HP</span>" : "") + "</td>" +
      '<td data-label="Duty">' + (r.duty === "heavy" || r.ndUnavailable ? "Heavy" : "Normal") +
      (r.enclosure ? '<span class="vc-mlist__sub">' + escapeHtml(r.enclosure) + "</span>" : "") + "</td>" +
      '<td class="is-num" data-label="Price"><span class="vc-mlist__price" data-vfd-price>…</span><span class="vc-mlist__stock" data-vfd-avail hidden></span></td>' +
      "</tr>"
    );
  }

  function enrichListRows(rows) {
    mapPool(rows, 4, function (tr) {
      return resolveCatalog(tr.getAttribute("data-vfd-model")).then(function (cat) {
        var price = tr.querySelector("[data-vfd-price]");
        var avail = tr.querySelector("[data-vfd-avail]");
        var link = tr.querySelector("[data-vfd-product-link]");
        if (!cat) {
          if (price) price.textContent = "Quote";
          return;
        }
        if (link && cat.url) link.href = cat.url;
        if (price) price.textContent = formatMoneyCents(cat.price) || "Quote";
        if (avail && cat.available === true) {
          avail.hidden = false;
          avail.textContent = "In stock";
        }
      });
    });
  }

  function renderFullList(root, rows, storeSearch) {
    var box = $(root, "[data-vfd-matches-list]");
    if (!box) return;
    var shown = 0;
    box.innerHTML =
      '<table class="vc-mlist__table"><thead><tr><th scope="col">Drive</th><th scope="col" class="is-num">Rating</th>' +
      '<th scope="col">Duty</th><th scope="col" class="is-num">Price</th></tr></thead><tbody></tbody></table>' +
      '<div><button type="button" class="vc-btn vc-btn--secondary" data-vfd-list-more hidden></button></div>';
    var tbody = $(box, "tbody");
    var more = $(box, "[data-vfd-list-more]");
    function page() {
      var next = rows.slice(shown, shown + LIST_PAGE);
      var tmp = document.createElement("tbody");
      tmp.innerHTML = next.map(function (r) {
        return listRowHtml(r, storeSearch);
      }).join("");
      var added = Array.prototype.slice.call(tmp.children);
      added.forEach(function (tr) {
        tbody.appendChild(tr);
      });
      enrichListRows(added);
      shown += next.length;
      var left = rows.length - shown;
      more.hidden = left <= 0;
      more.textContent = "Show " + Math.min(LIST_PAGE, left) + " more of " + left;
    }
    more.addEventListener("click", page);
    box._vfdPage = page;
    box._vfdRows = rows;
  }

  function renderMatches(root, s, sized, storeSearch, generation) {
    var d = sized.data;
    var grid = $(root, "[data-vfd-matches-grid]");
    var lead = $(root, "[data-vfd-matches-lead]");
    var empty = $(root, "[data-vfd-matches-empty]");
    var note = $(root, "[data-vfd-matches-note]");
    var moreWrap = $(root, "[data-vfd-matches-more]");
    var toggle = $(root, "[data-vfd-matches-toggle]");
    var list = $(root, "[data-vfd-matches-list]");
    var shipNote = root.getAttribute("data-ship-note") || "";
    if (!grid) return;

    var filtered = applyProtocolFilter(d, s.protocol);
    // The API catalogue also carries soft starters (e.g. Benshaw CSXi / EMX4e, source "...softstarter...").
    // They cannot vary speed, so they are never a VFD match.
    var all = flattenMatches(filtered.result.matches).filter(function (r) {
      return !isSoftStarter(r);
    });
    var sorted = all.slice().sort(function (a, b) {
      return fitScore(a, d) - fitScore(b, d);
    });
    var classLabel = voltageClassLabel(d.voltage && d.voltage.voltageClass, s.voltage);

    if (empty) {
      empty.hidden = true;
      empty.innerHTML = "";
    }
    if (list) {
      list.hidden = true;
      list.innerHTML = "";
    }
    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
    }
    if (note) {
      note.hidden = !filtered.note;
      note.textContent = filtered.note || "";
    }
    if (moreWrap) moreWrap.hidden = !sorted.length;
    if (toggle) toggle.textContent = "See all " + sorted.length + " matching drives";
    renderFullList(root, sorted, storeSearch);

    var see = $(root, '[data-vfd-out="seeLabel"]');
    if (!sorted.length) {
      grid.innerHTML = "";
      grid.setAttribute("aria-busy", "false");
      if (lead) lead.textContent = "No catalog drive covers " + fixed(d.requiredA, 1) + " A continuous on the " + classLabel + " class.";
      if (see) see.textContent = "See the options";
      renderEmpty(root, s, d, 0);
      return;
    }

    loadingCards(grid, CARD_TARGET);
    grid.setAttribute("aria-busy", "true");

    // Walk the fit order in small batches until CARD_TARGET in-stock, priced store products are found
    // (or RESOLVE_LIMIT rows are tried). Quote-only items stay in the full list, not in the cards.
    var found = [];
    var seen = {};
    var tried = 0;
    function batch() {
      if (String(root.getAttribute("data-vfd-gen")) !== String(generation)) return Promise.resolve(null);
      if (found.length >= CARD_TARGET || tried >= Math.min(RESOLVE_LIMIT, sorted.length)) return Promise.resolve(found);
      var rows = sorted.slice(tried, Math.min(tried + 8, RESOLVE_LIMIT));
      tried += rows.length;
      return mapPool(rows, 4, function (row) {
        return resolveCatalog(row.model).then(function (cat) {
          return { row: row, cat: cat };
        });
      }).then(function (resolved) {
        resolved.forEach(function (x) {
          if (!x || !x.cat || !x.cat.handle || seen[x.cat.handle]) return;
          if (x.cat.available !== true || !(Number(x.cat.price) > 0)) return;
          seen[x.cat.handle] = true;
          if (found.length < CARD_TARGET) found.push(x);
        });
        return batch();
      });
    }

    batch().then(function (ordered) {
      if (!ordered || String(root.getAttribute("data-vfd-gen")) !== String(generation)) return;
      var byModel = {};
      ordered.forEach(function (x) { byModel[x.row.model] = x.cat; });
      var picks = ordered.map(function (x, i) {
        var label = "";
        if (isWashdown(x.row)) label = "Washdown / IP66";
        else if (i > 0 && x.row.brand !== ordered[0].row.brand) label = "Alt brand";
        return { row: x.row, label: label, best: i === 0 };
      });

      return Promise.all(
        picks.map(function (p, i) {
          var cat = byModel[p.row.model];
          return fetchCard(cat.handle, cat.variantId).then(function (node) {
            if (!node) return null;
            var badge = fitBadge(p, d);
            var fit = node.querySelector("[data-vc-fit]");
            if (fit) {
              fit.textContent = badge.text;
              fit.hidden = false;
              fit.classList.toggle("vc-fit--best", badge.tone === "best");
            }
            var eyebrow = node.querySelector("[data-vc-eyebrow]");
            if (eyebrow) eyebrow.textContent = [p.row.brand, p.row.series].filter(Boolean).join(" · ");
            var specs = node.querySelector("[data-vc-specs]");
            if (specs) {
              specs.textContent = rowSpecs(p.row, d);
              specs.hidden = false;
            }
            return node;
          });
        })
      ).then(function (nodes) {
        if (String(root.getAttribute("data-vfd-gen")) !== String(generation)) return;
        var cards = nodes.filter(Boolean);
        grid.innerHTML = "";
        grid.setAttribute("aria-busy", "false");
        cards.forEach(function (node) {
          var li = document.createElement("li");
          li.appendChild(node);
          grid.appendChild(li);
        });
        var n = cards.length;
        if (lead) {
          lead.textContent = n
            ? (NUMBER_WORDS[n] || n) + (n === 1 ? " drive" : " drives") + " at or above " + fixed(d.requiredA, 1) +
              " A continuous on " + classLabel + ", sorted by fit. " + shipNote
            : sorted.length + " catalog drives cover " + fixed(d.requiredA, 1) + " A continuous on " + classLabel + ", none of them on the shelf right now.";
        }
        if (see) see.textContent = n ? "See " + n + " matching " + (n === 1 ? "drive" : "drives") : "See the options";
        if (!n) renderEmpty(root, s, d, sorted.length);
      });
    });
  }

  function resetMatches(root) {
    var grid = $(root, "[data-vfd-matches-grid]");
    if (grid) {
      grid.innerHTML = "";
      grid.setAttribute("aria-busy", "false");
    }
    ["[data-vfd-matches-empty]", "[data-vfd-matches-list]", "[data-vfd-matches-note]", "[data-vfd-matches-more]"].forEach(function (sel) {
      var el = $(root, sel);
      if (!el) return;
      el.hidden = true;
      if (sel !== "[data-vfd-matches-more]") el.innerHTML = "";
    });
    var toggle = $(root, "[data-vfd-matches-toggle]");
    if (toggle) toggle.setAttribute("aria-expanded", "false");
    setText(root, "[data-vfd-matches-lead]", "Enter the motor details above to see the drives on our shelf that fit.");
    setText(root, '[data-vfd-out="seeLabel"]', "See matching drives");
  }

  // ---------------------------------------------------------------------------
  // Photo badges, OCR, URL prefill
  // ---------------------------------------------------------------------------

  function setFieldBadge(root, name, kind, title) {
    var el = root.querySelector('[data-vfd-field="' + name + '"]');
    if (!el) return;
    var badge = el.querySelector("[data-vfd-field-badge]");
    if (!badge) return;
    badge.hidden = !kind;
    badge.textContent = title ? "· " + title : "";
    badge.classList.toggle("is-check", kind === "check");
  }

  function clearPhotoBadges(root) {
    $$(root, "[data-vfd-field-badge]").forEach(function (badge) {
      badge.hidden = true;
      badge.textContent = "";
      badge.classList.remove("is-check");
    });
    var banner = $(root, "[data-vfd-ocr-banner]");
    if (banner) banner.hidden = true;
    var hint = $(root, "[data-vfd-fla-hint]");
    if (hint) {
      hint.hidden = true;
      hint.textContent = "";
    }
  }

  function showOcrReading(root, fileName, pct) {
    var reading = $(root, "[data-vfd-ocr-reading]");
    var banner = $(root, "[data-vfd-ocr-banner]");
    if (banner) banner.hidden = true;
    if (!reading) return;
    reading.hidden = false;
    setText(reading, "[data-vfd-ocr-file]", fileName || "nameplate.jpg");
    var bar = $(reading, "[data-vfd-ocr-bar]");
    if (bar) bar.style.width = (pct || 8) + "%";
    setText(reading, "[data-vfd-ocr-pct]", "Reading the plate, " + (pct || 8) + "%");
  }

  function hideOcrReading(root) {
    var reading = $(root, "[data-vfd-ocr-reading]");
    if (reading) reading.hidden = true;
  }

  function showOcrBanner(root, filled, total, note) {
    var banner = $(root, "[data-vfd-ocr-banner]");
    if (!banner) return;
    banner.hidden = false;
    setText(banner, "[data-vfd-ocr-banner-text]", filled + " of " + total + " fields filled from the photo.");
    setText(banner, "[data-vfd-ocr-banner-sub]", note || "Review the values, then find matching drives.");
  }

  function setPowerUnit(root, unit, announce) {
    var span = $(root, "[data-vfd-power-unit]");
    if (span) span.textContent = unit === "kw" ? "kW" : "HP";
    if (announce) {
      var power = $(root, '[name="power"]');
      setText(
        root,
        "[data-vfd-unit-status]",
        "Power unit set to " + (unit === "kw" ? "kW" : "HP") + (power && power.value ? ". Motor power stays " + power.value + "." : ".")
      );
    }
  }

  /** Prefill from the savings calculator (?hp|kw, v, load, fla) and the VFD collection hero (?from=plp, ph). */
  function applyUrlPrefill(root, form) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return false;
    }
    var any = false;
    var badge = params.get("from") === "plp" ? "From collection" : "From savings";
    var hp = Number(params.get("hp"));
    var kw = Number(params.get("kw"));
    var volts = Number(params.get("v"));
    var load = params.get("load");
    var fla = Number(params.get("fla"));
    var ph = Number(params.get("ph"));
    var powerEl = $(form, '[name="power"]');
    var flaEl = $(form, '[name="fla"]');
    // A hand-off without a power value (savings page in measured volts/amps mode) must not leave the
    // example 20 HP / 27 A motor in place: clear it so the result reflects only what was passed.
    if (!(hp > 0 || kw > 0) && (volts > 0 || fla > 0 || load) && powerEl) {
      powerEl.value = "";
      if (flaEl) flaEl.value = "";
      any = true;
    }
    if ((hp > 0 || kw > 0) && powerEl) {
      powerEl.value = hp > 0 ? hp : kw;
      setRadio(form, "powerUnit", hp > 0 ? "hp" : "kw");
      setPowerUnit(root, hp > 0 ? "hp" : "kw", false);
      setFieldBadge(root, "power", "photo", badge);
      // the example FLA belongs to the example motor
      if (flaEl && !(fla > 0)) flaEl.value = "";
      any = true;
    }
    if (volts > 0 && setRadio(form, "supplyVoltage", String(nearestVoltage(volts)))) {
      setFieldBadge(root, "voltage", "photo", badge);
      any = true;
    }
    if ((load === "variable" || load === "constant" || load === "highInertia") && setRadio(form, "application", load)) {
      setFieldBadge(root, "application", "photo", badge);
      any = true;
    }
    if (fla > 0 && flaEl) {
      flaEl.value = fla;
      setFieldBadge(root, "fla", "photo", badge);
      any = true;
    }
    if ((ph === 1 || ph === 3) && setRadio(form, "phases", String(ph))) {
      setFieldBadge(root, "phases", "photo", badge);
      any = true;
    }
    return any;
  }

  // Sentences that promise a nameplate photo upload ([data-vfd-photo-copy], in the guide) show only while
  // the photo zone is enabled.
  function setPhotoCopy(enabled) {
    $$(document, "[data-vfd-photo-copy]").forEach(function (el) {
      el.hidden = !enabled;
      el.style.display = enabled ? "" : "none";
    });
  }

  // ---------------------------------------------------------------------------
  // init
  // ---------------------------------------------------------------------------

  function init(root) {
    if (!root || root.getAttribute("data-vfd-ready") === "1") return;
    root.setAttribute("data-vfd-ready", "1");

    var apiBase = (root.getAttribute("data-api-base") || "").replace(/\/$/, "").replace(/^http:\/\//i, "https://");
    var storeSearch = root.getAttribute("data-store-search") || "/search?q=";
    var form = $(root, "[data-vfd-form]");
    if (!apiBase || !form) {
      console.warn("[vfd-sizer] missing data-api-base or form");
      return;
    }

    var resultCard = $(root, "[data-vfd-result]");
    var calcError = $(root, "[data-vfd-calc-error]");
    var submitBtn = $(root, "[data-vfd-submit]");
    var clearBtn = $(root, "[data-vfd-clear]");
    var toggle = $(root, "[data-vfd-matches-toggle]");
    var photoWrap = $(root, "[data-vfd-photo-wrap]");
    var photoInput = $(root, "[data-vfd-photo]");
    var photoTake = $(root, "[data-vfd-photo-take]");
    var drop = $(root, "[data-vfd-drop]");
    var ocrStatus = $(root, "[data-vfd-ocr-status]");
    var cancelOcr = $(root, "[data-vfd-ocr-cancel]");
    var severalNote = $(root, "[data-vfd-several-note]");
    var ocrToken = null;
    var timer = null;
    var generation = 0;
    var submitted = false;

    function setCalcError(msg) {
      if (!calcError) return;
      calcError.hidden = !msg;
      var span = $(calcError, "span");
      if (span) span.textContent = msg || "";
    }

    /** Drop whatever request is in flight: its results must not land, and the UI must not stay busy. */
    function cancelPending() {
      generation++;
      root.setAttribute("data-vfd-gen", String(generation));
      if (resultCard) resultCard.classList.remove("is-pending");
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "Find matching drives";
      }
    }

    function run(opts) {
      opts = opts || {};
      var s = readState(form);
      var errors = validate(s);
      // Before the first submit only the fields being edited are flagged live; a blank power or FLA
      // (someone retyping) is not shouted at until they press Find matching drives.
      var shown = errors;
      if (!(opts.showErrors || submitted)) {
        shown = {};
        Object.keys(errors).forEach(function (k) {
          if (k !== "power" && k !== "fla") shown[k] = errors[k];
        });
      }
      showErrors(root, shown, !!opts.focus);
      if (Object.keys(errors).length) {
        cancelPending();
        if (!s.powerRaw) renderEmptyResult(root);
        else if (resultCard) resultCard.classList.add("is-pending"); // the card still shows the last valid inputs
        return;
      }
      setCalcError("");
      var gen = ++generation;
      root.setAttribute("data-vfd-gen", String(gen));
      if (resultCard) resultCard.classList.add("is-pending");
      if (opts.button && submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "Finding drives…";
      }
      sizeDrive(apiBase, s)
        .then(function (sized) {
          if (gen !== generation) return;
          renderResult(root, s, sized);
          renderMatches(root, s, sized, storeSearch, gen);
          if (opts.scroll) {
            var target = document.getElementById("matches");
            if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        })
        .catch(function (err) {
          if (gen !== generation) return;
          setCalcError((err && err.message) || "Could not size the drive. Check your connection and try again.");
        })
        .then(function () {
          if (gen !== generation) return;
          if (resultCard) resultCard.classList.remove("is-pending");
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = "Find matching drives";
          }
        });
    }

    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        run({});
      }, DEBOUNCE_MS);
    }

    form.addEventListener("input", schedule);
    form.addEventListener("change", function (e) {
      var t = e.target;
      if (t && t.name === "powerUnit") setPowerUnit(root, t.value, true);
      if (t && t.name === "motors" && severalNote) severalNote.hidden = t.value !== "several";
      if (t && t.name === "phases") {
        // 1-phase only exists at 208/230 V: steer the voltage instead of erroring
        var v = Number(radioValue(form, "supplyVoltage"));
        if (t.value === "1" && v >= 380) setRadio(form, "supplyVoltage", "230");
      }
      schedule();
    });
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      clearTimeout(timer);
      submitted = true;
      run({ showErrors: true, focus: true, button: true, scroll: true });
    });

    if (clearBtn) {
      clearBtn.addEventListener("click", function () {
        clearTimeout(timer);
        form.reset();
        ["power", "fla"].forEach(function (n) {
          var el = $(form, '[name="' + n + '"]');
          if (el) el.value = "";
        });
        var alt = $(form, '[name="altitudeM"]');
        if (alt) alt.value = "0";
        setPowerUnit(root, "hp", false);
        if (severalNote) severalNote.hidden = true;
        clearPhotoBadges(root);
        submitted = false;
        showErrors(root, {}, false);
        setCalcError("");
        cancelPending();
        renderEmptyResult(root);
        resetMatches(root);
        var power = $(form, '[name="power"]');
        if (power) power.focus();
      });
    }

    if (toggle) {
      toggle.addEventListener("click", function () {
        var list = $(root, "[data-vfd-matches-list]");
        if (!list) return;
        var open = toggle.getAttribute("aria-expanded") === "true";
        toggle.setAttribute("aria-expanded", open ? "false" : "true");
        list.hidden = open;
        if (!open && list._vfdPage && !list.querySelector("tbody tr")) list._vfdPage();
        if (!open) {
          var first = list.querySelector("a");
          if (first) first.focus({ preventScroll: false });
        }
      });
    }

    // --- Nameplate photo -----------------------------------------------------
    setPhotoCopy(false);
    fetch(apiBase + "/api/vfd-size/ocr", { headers: apiHeaders() })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (photoWrap && data && data.photoEnabled) {
          photoWrap.hidden = false;
          setPhotoCopy(true);
        }
      })
      .catch(function () {});

    function applyOcrResult(d) {
      var filled = 0;
      var total = 5;
      // The OCR route reports confidence per field (0-1); below 0.7 the field is badged "Check this".
      // Older response shapes (uncertain / flags / flaUncertain) are still honoured.
      var uncertain = d.uncertain || d.flags || {};
      if (Array.isArray(uncertain)) {
        var map = {};
        uncertain.forEach(function (k) {
          map[k] = true;
        });
        uncertain = map;
      }
      var conf = d.confidence || {};
      Object.keys(conf).forEach(function (k) {
        if (Number(conf[k]) < 0.7) uncertain[k] = true;
      });
      var powerEl = $(form, '[name="power"]');
      var flaEl = $(form, '[name="fla"]');
      if (d.hp != null && d.hp > 0 && powerEl) {
        powerEl.value = d.hp;
        setRadio(form, "powerUnit", "hp");
        setPowerUnit(root, "hp", false);
        setFieldBadge(root, "power", "photo", "From photo");
        filled++;
      } else if (d.kw != null && d.kw > 0 && powerEl) {
        powerEl.value = d.kw;
        setRadio(form, "powerUnit", "kw");
        setPowerUnit(root, "kw", false);
        setFieldBadge(root, "power", "photo", "From photo");
        filled++;
      }
      if (flaEl) flaEl.value = "";
      if (d.fla != null && d.fla > 0 && flaEl) {
        flaEl.value = d.fla;
        if (uncertain.fla || d.flaUncertain) {
          setFieldBadge(root, "fla", "check", "Check this");
          var hint = $(root, "[data-vfd-fla-hint]");
          if (hint) {
            hint.hidden = false;
            hint.textContent = d.flaNote || "Read as " + d.fla + (d.flaAlt ? " or " + d.flaAlt : "") + " A. Confirm against the plate.";
          }
        } else {
          setFieldBadge(root, "fla", "photo", "From photo");
        }
        filled++;
      }
      if (d.voltage != null && d.voltage > 0 && setRadio(form, "supplyVoltage", String(nearestVoltage(d.voltage)))) {
        setFieldBadge(root, "voltage", "photo", "From photo");
        filled++;
      }
      if ((d.phases === 1 || d.phases === 3) && setRadio(form, "phases", String(d.phases))) {
        setFieldBadge(root, "phases", "photo", "From photo");
        filled++;
      }
      // Only snap to a chip that exists (1.10 etc. used to send serviceFactor 0)
      if (d.serviceFactor != null && setRadio(form, "serviceFactor", String(Number(d.serviceFactor)))) {
        setFieldBadge(root, "sf", "photo", "From photo");
        filled++;
      }
      setFieldBadge(root, "application", "choose", "You choose");
      showOcrBanner(
        root,
        filled,
        total,
        uncertain.fla || d.flaUncertain ? "Full-load amps needs your confirmation." : "Review the values, then find matching drives."
      );
      if (ocrStatus) {
        ocrStatus.textContent = "";
        ocrStatus.classList.remove("is-error");
      }
      schedule();
    }

    function runOcr(file) {
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        if (ocrStatus) {
          ocrStatus.textContent = "That photo is over 10 MB. Try a smaller one.";
          ocrStatus.classList.add("is-error");
        }
        return;
      }
      clearPhotoBadges(root);
      if (ocrStatus) {
        ocrStatus.textContent = "";
        ocrStatus.classList.remove("is-error");
      }
      if (ocrToken) ocrToken.aborted = true;
      var token = { aborted: false };
      ocrToken = token;
      var pct = 12;
      showOcrReading(root, file.name, pct);
      var tick = setInterval(function () {
        if (token.aborted) return clearInterval(tick);
        pct = Math.min(92, pct + 8);
        showOcrReading(root, file.name, pct);
      }, 280);
      compressImage(file)
        .then(function (blob) {
          if (token.aborted) throw new Error("__abort__");
          var body = new FormData();
          body.append("image", blob, "nameplate.jpg");
          return fetch(apiBase + "/api/vfd-size/ocr", { method: "POST", headers: apiHeaders(), body: body });
        })
        .then(function (r) {
          return r.json().then(function (data) {
            return { ok: r.ok, data: data };
          });
        })
        .then(function (res) {
          clearInterval(tick);
          if (token.aborted) return;
          hideOcrReading(root);
          if (!res.ok) {
            if (ocrStatus) {
              ocrStatus.textContent = (res.data && res.data.error) || "We could not read that plate. Type the values below.";
              ocrStatus.classList.add("is-error");
            }
            return;
          }
          applyOcrResult(res.data);
        })
        .catch(function (err) {
          clearInterval(tick);
          hideOcrReading(root);
          if (token.aborted || (err && err.message === "__abort__")) return;
          if (ocrStatus) {
            ocrStatus.textContent = (err && err.message) || "Could not process the photo.";
            ocrStatus.classList.add("is-error");
          }
        });
    }

    [photoInput, photoTake].forEach(function (input) {
      if (!input) return;
      input.addEventListener("change", function () {
        var file = input.files && input.files[0];
        input.value = "";
        runOcr(file);
      });
    });
    if (cancelOcr) {
      cancelOcr.addEventListener("click", function () {
        if (ocrToken) ocrToken.aborted = true;
        hideOcrReading(root);
      });
    }
    if (drop) {
      ["dragenter", "dragover"].forEach(function (type) {
        drop.addEventListener(type, function (e) {
          e.preventDefault();
          drop.classList.add("is-dragover");
        });
      });
      ["dragleave", "drop"].forEach(function (type) {
        drop.addEventListener(type, function (e) {
          e.preventDefault();
          drop.classList.remove("is-dragover");
        });
      });
      drop.addEventListener("drop", function (e) {
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file && /^image\//.test(file.type)) runOcr(file);
      });
    }

    // --- First paint: URL hand-off or the example ---------------------------
    applyUrlPrefill(root, form);
    run({});
  }

  function boot() {
    document.querySelectorAll("[data-vfd-sizer]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector("[data-vfd-sizer]") : null;
    if (root) {
      root.removeAttribute("data-vfd-ready");
      init(root);
    }
  });
})();
